import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {JsonSchema} from './schema-form/schema-form.types';
import {SfValueBindingComponent} from './schema-form/widgets/sf-value-binding.component';
import {InputBinding, ResolvedRef, UpstreamVariable} from './workflow.models';

/**
 * Die Bindung eines EINGANGS: Festwert eintragen oder einen Wert aus einem vorherigen Schritt
 * wählen. Ersetzt das Ziehen einer Datenkante.
 *
 * Die Bedienung selbst steckt in {@link SfValueBindingComponent} — dieselbe Komponente rendert
 * über `x-widget: 'value-binding'` auch bindbare **Einstellungen** (Wert A/Wert B je Fall des
 * Bedingungs-Moduls). Diese Hülle hier übersetzt nur zwischen den Workflow-Typen des Panels
 * ({@link InputBinding}, {@link ResolvedRef}, {@link UpstreamVariable}) und der workflow-freien
 * Form des Widgets; die schema-form-Schicht bleibt dadurch frei von Workflow-Wissen.
 *
 * `UpstreamVariable`/`ResolvedRef` erfüllen die Widget-Typen strukturell, es wird also nichts
 * umkopiert — nur zugewiesen.
 */
@Component({
  selector: 'app-workflow-value-binding',
  standalone: true,
  imports: [SfValueBindingComponent],
  host: {'class': 'block w-full'},
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col gap-1">
      @if (showLabel && label) {
        <label class="text-sm font-medium text-gray-900">{{ label }}</label>
      }
      @if (description) {
        <p class="text-sm text-gray-500 leading-tight">{{ description }}</p>
      }
      <sf-value-binding
        [schema]="schema"
        [value]="binding.raw"
        [hint]="hint"
        [candidates]="candidates"
        [source]="source"
        [readOnly]="readOnly"
        (literalChange)="literalChange.emit($event)"
        (referenceChange)="referenceChange.emit($event)"
        (cleared)="cleared.emit()">
      </sf-value-binding>
    </div>
  `,
})
export class WorkflowValueBindingComponent {
  /** Beschriftung des Werts (Port-Titel des Eingangs bzw. „Var1" beim Formel-Modul). */
  @Input() label = '';
  @Input() showLabel = true;
  @Input() description = '';
  /** Hilfszeile unter dem Festwert-Feld (Beschreibung + Bindungsstatus). */
  @Input() hint = '';
  /** Schema des Eingangs — treibt das Festwert-Widget. */
  @Input() schema: JsonSchema = {};
  @Input() binding: InputBinding = {kind: 'none', raw: undefined};
  /**
   * Aufgelöste Quelle, wenn `binding.kind === 'reference'`.
   *
   * Vorrang vor der Kandidatenliste: eine Bestandsreferenz kann auf einen Knoten zeigen, der
   * kein Fluss-Vorgänger ist — der Picker bietet sie dann nicht mehr an, angezeigt werden muss
   * sie trotzdem (siehe `resolveVariableReference`).
   */
  @Input() source: ResolvedRef | null = null;
  /** Wählbare Werte — nur Ausgaben von Schritten, die im Ablauf VOR diesem liegen. */
  @Input() candidates: UpstreamVariable[] = [];
  @Input() readOnly = false;

  @Output() literalChange = new EventEmitter<any>();
  @Output() referenceChange = new EventEmitter<string>();
  @Output() cleared = new EventEmitter<void>();
}
